import { memo, useState } from 'react';
import { Link } from 'react-router-dom';

import type { ConnaroEvent, PlatformName, TimestampFormat } from '../api/types.js';
import { usePreferences } from './use-preferences.js';
import { nameColor } from './name-colors.js';
import { twitchBadgeSetTitle, twitchBadgeTitle } from './twitch-badges.js';
import { usePaints } from './use-paints.js';
import { PlatformIcon } from '../components/PlatformIcon.js';
import { segmentMentions } from './feed.js';
import { segmentEmotes } from './emotes.js';
import { platformLabel } from '../platforms/labels.js';

/**
 * One row in the unified feed (ARCHITECTURE.md §22, §56, §57).
 *
 * SECURITY: every provider-supplied value below reaches the DOM as a React text
 * node or as an attribute React escapes. There is no `dangerouslySetInnerHTML`
 * anywhere in this file, and there must never be one — chat is untrusted
 * user-generated content, and rendering it as HTML is the exact defect §56
 * exists to prevent.
 *
 * Normalisation upstream (§57) already stripped control characters, bidi
 * overrides and zero-width padding, and capped lengths. This layer's job is to
 * ESCAPE, not to sanitize again: doing both would double-encode.
 */

/**
 * How each platform is named in the feed.
 *
 * Typed as a COMPLETE record rather than `Record<string, string>`, so adding a
 * platform without naming it fails the build. It was previously partial, and
 * Discord shipped without an entry — live Discord messages rendered the raw
 * lowercase key `discord` beside every one.
 *
 * A fallback to `event.platform` hid that: the row looked almost right, so
 * nothing drew attention to it.
 *
 * The name is no longer PRINTED on the row — the brand mark stands in its
 * place — but it is still the icon's accessible name, so it remains the one
 * thing identifying the platform to anyone not looking at the pixels. Getting
 * it wrong is now less visible, not less important.
 */

/** Non-chat events get a short description rather than an empty row. */
const EVENT_LABELS: Record<string, string> = {
  'chat.deleted': 'deleted a message',
  'user.joined': 'joined',
  follow: 'followed',
  subscription: 'subscribed',
  membership: 'became a member',
  gift: 'gifted a subscription',
  donation: 'donated',
  raid: 'raided the channel',
  system: 'system event',
};

/**
 * The five formats an operator can choose, as Intl options.
 *
 * A closed map rather than a format string from storage: a template would let
 * a stored value decide what `Intl.DateTimeFormat` renders, and the set of
 * things it could be asked for is unbounded.
 */
const TIME_OPTIONS: Record<Exclude<TimestampFormat, 'hidden'>, Intl.DateTimeFormatOptions> = {
  '24h_seconds': { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false },
  '24h': { hour: '2-digit', minute: '2-digit', hour12: false },
  '12h_seconds': { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true },
  '12h': { hour: 'numeric', minute: '2-digit', hour12: true },
};

function formatTime(iso: string, timeZone: string | undefined, format: TimestampFormat): string {
  if (format === 'hidden') return '';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  // §45: stored UTC, displayed in the operator's timezone.
  return new Intl.DateTimeFormat(undefined, {
    ...TIME_OPTIONS[format],
    ...(timeZone === undefined ? {} : { timeZone }),
  }).format(date);
}

export type ChatMessageProps = {
  event: ConnaroEvent;
  /** Operator's own handle, for mention highlighting. */
  mentionHandle: string | undefined;
  mentioned: boolean;
  timeZone: string | undefined;
  /**
   * Connaro Moderator bridge (§98, module brief §17).
   *
   * Off unless the actor holds `moderation.view` — a link to a page they
   * cannot open is worse than no link. The bridge is deliberately minimal:
   * Moderator is the workspace, and duplicating its actions into every chat
   * row would put the same provider call behind two very different amounts of
   * context about who is being acted on.
   */
  moderation?: boolean;
  /** True when this message already produced a finding. */
  flagged?: boolean;
  /**
   * Loaded from stored history rather than delivered live.
   *
   * Dimmed, but never ONLY dimmed: the row keeps its full text and the feed
   * separates the two groups by order, so an operator who cannot perceive the
   * difference in tone loses nothing (§65).
   */
  fromHistory?: boolean;
};

/**
 * The speaker's picture, or their initial.
 *
 * ======================== WHY IT GOES THROUGH CONNARO ========================
 * The Console's CSP is `img-src 'self' data:`, so a provider CDN URL is refused
 * by the browser — this row rendered one for months and showed nothing at all.
 *
 * Widening the CSP would have been the quick fix and the wrong one:
 * `avatarUrl` is provider-supplied input on a page that renders untrusted chat,
 * and an `img-src` allowlist would let a payload name any host in it. The proxy
 * moves that decision to the server, where the host is checked against an
 * allowlist rather than declared in a header.
 *
 * The initial remains the fallback, for three real cases: no avatar at all, a
 * host Connaro will not fetch from, and an image that fails to load. A broken
 * image icon in a chat feed is worse than a letter.
 */
function Avatar({
  name,
  avatarUrl,
}: {
  name: string;
  avatarUrl: string | undefined;
}): React.JSX.Element {
  const [failed, setFailed] = useState(false);
  const initial = name.slice(0, 1).toUpperCase();

  if (avatarUrl === undefined || failed) {
    return (
      <span className="chat-row__avatar" aria-hidden="true">
        {initial}
      </span>
    );
  }

  return (
    <img
      className="chat-row__avatar chat-row__avatar--image"
      src={`/api/v1/avatars?url=${encodeURIComponent(avatarUrl)}`}
      alt=""
      aria-hidden="true"
      loading="lazy"
      decoding="async"
      onError={() => {
        setFailed(true);
      }}
    />
  );
}

/**
 * One badge: the real image where Connaro ships it, the label otherwise.
 *
 * ============================ WHY PLATFORM-SCOPED ============================
 * These are TWITCH's badges. Kick, YouTube and Discord have their own, and
 * their badge ids collide with Twitch's — every one of them calls a moderator
 * `moderator`. Drawing Twitch's sword-and-shield beside a Discord moderator
 * would assert something about Discord that Twitch's asset does not say, so
 * the image is used only where the platform matches the asset.
 *
 * The label is not decoration being replaced by a picture: the image carries
 * the badge's own title as its accessible name, so "Moderator" is still
 * announced. Dropping to `alt=""` here would delete role information that the
 * text pill used to convey.
 */
function Badge({
  badge,
  platform,
}: {
  badge: { id: string; label?: string | undefined; version?: string | undefined };
  platform: PlatformName;
}): React.JSX.Element {
  const twitch = platform === 'twitch';
  const title = twitch ? twitchBadgeTitle(badge.id, badge.version) : undefined;

  if (title === undefined) {
    /*
     * No image for this exact version. Prefer the provider's label, then the
     * SET's name — a channel's 12-month subscriber badge is not in Twitch's
     * global set, so `subscriber` would otherwise reach the row as a raw id.
     */
    const fallback =
      badge.label ?? (twitch ? twitchBadgeSetTitle(badge.id) : undefined) ?? badge.id;

    return <span className="chat-row__badge">{fallback}</span>;
  }

  return (
    <img
      className="chat-row__badge chat-row__badge--image"
      src={`/badges/${encodeURIComponent(badge.id)}/${encodeURIComponent(badge.version ?? '1')}.png`}
      alt={title}
      title={title}
      width={18}
      height={18}
      loading="lazy"
      decoding="async"
    />
  );
}

export const ChatMessage = memo(function ChatMessage({
  event,
  mentionHandle,
  mentioned,
  timeZone,
  moderation = false,
  flagged = false,
  fromHistory = false,
}: ChatMessageProps): React.JSX.Element {
  const { preferences, emotes } = usePreferences();
  const { paintFor } = usePaints();
  const actor = event.actor;
  const name = actor?.displayName ?? actor?.username ?? 'Unknown';
  const text = event.message?.text;
  const label = platformLabel(event.platform);

  /*
   * `metadata.channelName` is provider-derived text reaching the DOM, so it is
   * narrowed to a string here and rendered as a text node like everything else
   * (§56). It is never trusted to be present, or to be a string.
   */
  const rawChannelName = event.metadata?.['channelName'];
  const channelName =
    typeof rawChannelName === 'string' && rawChannelName !== '' ? rawChannelName : undefined;

  return (
    <li
      className={`chat-row${mentioned ? ' chat-row--mention' : ''}${flagged ? ' chat-row--flagged' : ''}${fromHistory ? ' chat-row--history' : ''}`}
      data-message-id={event.id}
      data-platform={event.platform}
    >
      {preferences.timestampFormat === 'hidden' ? null : (
        <time className="chat-row__time" dateTime={event.occurredAt}>
          {formatTime(event.occurredAt, timeZone, preferences.timestampFormat)}
        </time>
      )}

      {/*
       * The brand mark, standing in for the name.
       *
       * A mixed feed is scanned rather than read: an operator finds one
       * platform's messages by shape and colour long before they finish
       * reading a word, and a text pill on every row costs horizontal space
       * on the narrowest surface Connaro has — the OBS dock.
       *
       * `label` is what makes this legitimate rather than a regression.
       * PlatformIcon is decorative by default precisely because it usually
       * sits beside the name; here it IS the name, so it carries it as an
       * accessible name and a screen reader still announces "Twitch".
       */}
      {!preferences.showPlatformIcons ? null : (
        <span className={`chat-row__platform chat-row__platform--${event.platform}`}>
          <PlatformIcon
            platform={event.platform}
            displayName={label}
            label={label}
            size={16}
          />
        </span>
      )}

      <span className="chat-row__author">
        {/*
         * Which channel it came from, where the platform has more than one.
         *
         * Sits BEFORE the speaker rather than in front of the message, so the
         * colon below stays against the text it introduces. `#general Someone:
         * hello` reads; `Someone: #general hello` looks like the channel is
         * part of what they said.
         *
         * Only rendered when the normalizer supplied a name: Twitch and Kick
         * have one channel per connection, and prefixing every line with it
         * would be noise.
         */}
        {channelName === undefined ? null : (
          <span className="chat-row__channel">#{channelName}</span>
        )}
        {!preferences.showProfilePictures ? null : (
          <Avatar name={name} avatarUrl={actor?.avatarUrl} />
        )}

        {/*
         * Badges come BEFORE the name, which is what every chat client does
         * and what the punctuation needs.
         *
         * After the name they sat between it and the colon, and a badge is a
         * bordered box — punctuation after a box always detaches, so the row
         * read `ModHelper MOD : hello` with a gap no negative margin could
         * close without overlapping the badge's own background. Before the
         * name, the colon meets the name directly and the badge reads as the
         * prefix it is.
         */}
        {actor?.badges?.map((badge) => (
          <Badge
            key={`${badge.id}-${badge.version ?? ''}`}
            badge={badge}
            platform={event.platform}
          />
        ))}
        {(() => {
          /*
           * A paint REPLACES the flat colour rather than tinting it: it is a
           * gradient clipped to the text, so a `color` underneath would only
           * show through where the gradient is transparent.
           *
           * The two declarations were built by Connaro from 7TV's numeric
           * fields — see `services/paints.ts`. Nothing 7TV sent as a string
           * reaches this style object.
           */
          const paint = event.platform === 'twitch' ? paintFor(actor?.platformUserId) : undefined;

          return (
            <span
              className={`chat-row__name${paint === undefined ? '' : ' chat-row__name--painted'}`}
              title={paint?.name}
              style={
                paint === undefined
                  ? {
                      /*
                       * Keyed on the USERNAME, not the display name: a person
                       * can change how their name is shown and would otherwise
                       * change colour with it.
                       */
                      color: nameColor(actor?.username ?? name, preferences.randomNameColors),
                    }
                  : {
                      backgroundImage: paint.backgroundImage,
                      ...(paint.textShadow === undefined
                        ? {}
                        : { filter: `drop-shadow(${paint.textShadow})` }),
                    }
              }
            >
              {name}
            </span>
          );
        })()}
        {/*
         * The colon is OUTSIDE the name span, and it is LAST.
         *
         * Outside, because inside it would become part of the accessible name
         * and part of any text selection — copying a username out of the feed
         * would carry a stray colon into whatever it was pasted into.
         *
         * Last, because its job is to introduce the message and the name is
         * now the last thing before it. An earlier arrangement put badges
         * after the name, which forced the colon behind them and read
         * `ModHelper MOD : hello` — a role looking like the first word the
         * person said, with a gap the badge's own padding kept open.
         */}
        <span className="chat-row__colon" aria-hidden="true">
          :
        </span>
      </span>

      <span className="chat-row__body">
        {typeof text !== 'string' || text === '' ? (
          <em className="chat-row__event">{EVENT_LABELS[event.type] ?? event.type}</em>
        ) : (
          /*
           * Index keys are correct here, unusually. The segments are a pure
           * function of one immutable message's text: they are never
           * reordered, inserted into, or removed, so an index IS a stable
           * identity for the life of the row.
           */
          /*
           * Mentions first, then emotes WITHIN the non-mention runs. Doing it
           * the other way would let an emote name inside a mention split the
           * handle, and a mention is the more important of the two to keep
           * intact.
           */
          segmentMentions(text, mentionHandle).map((segment, index) =>
            segment.mention ? (
              <mark key={index} className="chat-row__mention">
                {segment.text}
              </mark>
            ) : (
              <span key={index}>
                {segmentEmotes(segment.text, emotes).map((piece, at) =>
                  piece.kind === 'emote' ? (
                    <img
                      key={at}
                      className="chat-row__emote"
                      /*
                       * The size token differs by provider: 7TV and BetterTTV
                       * publish `2x`, FrankerFaceZ publishes `2`. Asking FFZ
                       * for `2x` is a 404 and an empty square in the feed.
                       */
                      src={`/api/v1/emotes/${piece.provider}/${piece.id}/${
                        piece.provider === 'ffz' ? '2' : '2x'
                      }`}
                      /*
                       * The emote's NAME is its accessible name, because that
                       * is what the person typed. `alt=""` would delete a word
                       * from the message for anyone not looking at it.
                       */
                      alt={piece.name}
                      title={piece.name}
                      style={{ height: preferences.emoteSize }}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <span key={at}>{piece.text}</span>
                  ),
                )}
              </span>
            ),
          )
        )}

        {event.monetary?.displayAmount !== undefined && (
          <span className="chat-row__amount">{event.monetary.displayAmount}</span>
        )}

        {/*
         * Stated in words, not by the stripe alone (§65). An operator scanning
         * a fast feed should not have to notice a colour to learn that Connaro
         * already flagged this line.
         */}
        {flagged && <span className="chat-row__flagged">Flagged</span>}
      </span>

      {moderation && preferences.showQuickActions && (
        <Link
          className="chat-row__moderate"
          to={
            event.actor?.platformUserId === undefined
              ? '/moderator'
              : `/moderator?platformUserId=${encodeURIComponent(event.actor.platformUserId)}`
          }
        >
          Open in Moderator
          <span className="visually-hidden"> for {name}</span>
        </Link>
      )}
    </li>
  );
});
